"use client";

import { useState } from "react";

import { Button } from "@/components/button/button.component";

import { AnswerProps } from "../quizFlow.types";
import { QuizQuestionHelperText } from "./quizQuestionHelperText.component";

export const NumberInput = ({
  question,
  onNumberInputNext,
  initialAnswer,
}: AnswerProps) => {
  const config = question.numberInputConfig;
  const min = config?.min ?? 0;
  const max = config?.max ?? 99;
  const defaultVal = config?.defaultValue ?? 1;
  const [value, setValue] = useState(
    typeof initialAnswer === "number" ? initialAnswer : defaultVal,
  );

  if (!config) return null;

  const decrement = () => {
    setValue((prev) => Math.max(min, prev - 1));
  };

  const increment = () => {
    setValue((prev) => Math.min(max, prev + 1));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseInt(e.target.value, 10);
    if (!isNaN(parsed)) {
      setValue(Math.max(min, Math.min(max, parsed)));
    } else if (e.target.value === "") {
      setValue(min);
    }
  };

  return (
    <div
      data-testid="number-input"
      className="mx-auto flex w-full max-w-md flex-col items-center gap-8 pb-24 md:pb-0">
      <div className="flex items-center gap-4">
        <button
          data-testid="number-decrement"
          onClick={decrement}
          disabled={value <= min}
          aria-label="Decrease quantity"
          className="border-greyscale-strength text-greyscale-strength flex h-10 w-10 items-center justify-center rounded-full border text-xl font-thin transition-colors hover:bg-gray-50 disabled:opacity-30">
          &minus;
        </button>

        <input
          aria-label={`Number input for question "${question.questionKey}"`}
          data-testid="number-input-field"
          type="text"
          inputMode="numeric"
          value={value}
          onChange={handleChange}
          className="text-greyscale-headline focus:border-secondary h-12 w-40 rounded-full border border-gray-300 text-center text-lg font-medium outline-none"
        />

        <button
          data-testid="number-increment"
          onClick={increment}
          disabled={value >= max}
          aria-label="Increase quantity"
          className="border-greyscale-strength text-greyscale-strength flex h-10 w-10 items-center justify-center rounded-full border text-xl font-thin transition-colors hover:bg-gray-50 disabled:opacity-30">
          +
        </button>
      </div>

      <QuizQuestionHelperText text={question.helperText} />

      <div className="fixed right-0 bottom-0 left-0 z-10 flex justify-center bg-white/90 px-5 py-4 backdrop-blur-sm md:static md:bg-transparent md:px-0 md:py-0 md:backdrop-blur-none">
        <Button
          secondary
          data-testid="quiz-next-button"
          onClick={() => onNumberInputNext(value)}
          className="w-full md:w-auto md:min-w-[200px]">
          NEXT
        </Button>
      </div>
    </div>
  );
};
